import { Text } from '@airwallex/typography';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { formatCurrency } from '@airwallex/awx-currency-formatter';
import { IconChevronDown, IconChevronUp } from '@airwallex/glyphs';
import { cellContainerStyle, ShowMoreButton, LineItemsWrapper } from './v1/style';
import { useTranslation } from 'react-i18next';
import { useMobile, useTabletOrMobile } from '@/hooks/useMediaQuery';
import { StoreContext } from '@/providers/storeProvider';
import { formatDate, DATE_PRECISION, DATE_STYLE } from '@/utils/formatDate';
import i18n from '@/utils/i18n';
import { LineItems as LineItemsComponent, LineItemsColumn, LineItemsDataSource } from '@airwallex/line-items';
import DiscountCell from '@/common/DiscountCell';
import { VStack } from '@airwallex/stacks';
import { formatPriceCurrency } from '@billing/billing-domain-shared';

type LineItemColumnKey = 'product' | 'quantity' | 'price' | 'amount';

const SHOW_MORE_LENGTH = 4;

const LineItems: React.FC = () => {
  const { t } = useTranslation();
  const { invoiceData } = useContext(StoreContext);
  const { invoice, lineItemGroups } = invoiceData;
  const { currency } = invoice;
  const isMobile = useMobile();
  const isTabletOrMobile = useTabletOrMobile();
  const [showAllItems, setShowAllItems] = useState(false);

  const visibleGroups = useMemo(() => {
    if (isTabletOrMobile || lineItemGroups.length <= SHOW_MORE_LENGTH) {
      return lineItemGroups;
    }
    return showAllItems ? lineItemGroups : lineItemGroups.slice(0, SHOW_MORE_LENGTH);
  }, [isTabletOrMobile, lineItemGroups, showAllItems]);

  const showMoreButtonShown = useMemo(() => {
    if (isTabletOrMobile || lineItemGroups.length <= SHOW_MORE_LENGTH) {
      return false;
    }
    return lineItemGroups.length > SHOW_MORE_LENGTH;
  }, [lineItemGroups, isTabletOrMobile]);

  useEffect(() => {
    setShowAllItems(isMobile);
  }, [isMobile]);

  const fecColumns: LineItemsColumn<LineItemColumnKey>[] = [
    { key: 'product', header: t('invoice.product') },
    { key: 'quantity', header: t('invoice.quantity') },
    { key: 'price', header: t('invoice.price'), align: 'right' },
    { key: 'amount', header: t('invoice.amount'), align: 'right' },
  ];

  const fecDataSources: LineItemsDataSource<LineItemColumnKey>[] = useMemo(() => {
    const rows: LineItemsDataSource<LineItemColumnKey>[] = [];

    visibleGroups.forEach((item) => {
      rows.push({
        product: (
          <VStack style={cellContainerStyle}>
            <Text variant="body">{item.label}</Text>
            {item.periodStart && item.periodEnd && (
              <Text variant="hint" color="secondary">
                {t('invoice.period', {
                  start: formatDate(item.periodStart, i18n.language, DATE_STYLE.NATURAL, DATE_PRECISION.DATE_ONLY),
                  end: formatDate(item.periodEnd, i18n.language, DATE_STYLE.NATURAL, DATE_PRECISION.DATE_ONLY),
                })}
              </Text>
            )}
          </VStack>
        ),
        quantity: <Text variant="body">{item.totalUsageVolume}</Text>,
        price: (
          <Text variant="body">
            {typeof item.unitAmount === 'number'
              ? formatPriceCurrency({
                  amount: item.unitAmount,
                  currency: currency,
                  showSymbol: true,
                  showCode: false,
                })
              : t('invoice.priceVaries')}
          </Text>
        ),
        amount: (
          <Text variant="body">
            {formatCurrency({
              amount: item.totalAmount,
              currency: currency,
              showSymbol: true,
              showCode: false,
            })}
          </Text>
        ),
      });

      if (item.lineItemDiscounts && item.lineItemDiscounts.length > 0) {
        item.lineItemDiscounts.forEach((discount) => {
          rows.push({
            product: <DiscountCell discount={discount} variant="label" useVStack={false} />,
            quantity: null,
            price: null,
            amount: <DiscountCell discount={discount} variant="amount" currency={currency} useVStack={false} />,
          });
        });
      }
    });

    if (showMoreButtonShown) {
      rows.push({
        product: (
          <ShowMoreButton
            iconAfter={showAllItems ? IconChevronUp : IconChevronDown}
            onClick={() => setShowAllItems(!showAllItems)}
          >
            {showAllItems ? t('invoice.showLessLineItems') : t('invoice.showMoreLineItems')}
          </ShowMoreButton>
        ),
        quantity: '',
        price: '',
        amount: '',
      });
    }

    return rows;
  }, [visibleGroups, currency, t, showMoreButtonShown, showAllItems]);

  return (
    <LineItemsWrapper showMoreButtonShown={showMoreButtonShown}>
      <LineItemsComponent isMobile={isTabletOrMobile} columns={fecColumns} dataSources={fecDataSources} />
    </LineItemsWrapper>
  );
};

export default LineItems;
